<template>
  <div class="modern-main">
    <!-- Layer A: 全局统一背景层 (Canvas) -->
    <div class="app-background-layer"></div>

    <!-- 固定标题栏 -->
    <AppHeader
      @theme-toggle="handleThemeToggle"
      @open-api-site="handleOpenApiSite"
      @logout="handleLogout"
    />

    <!-- 两列布局：文本编辑区(中间) + 语音主播选择区(右侧)  v-show="page.asideIndex === '1'" -->
    <div class="two-column-layout">
      <!-- router路由渲染page，使用keep-alive支持根据meta缓存组件 -->
      <router-view class="router-view" v-slot="{ Component }">
        <keep-alive>
          <component :is="Component" v-if="$route.meta.keepAlive" :key="$route.path" />
        </keep-alive>
        <component :is="Component" v-if="!$route.meta.keepAlive" :key="$route.path" />
      </router-view>
    </div>

    <!-- 备案信息 - 只在指定页面显示 -->
    <footer v-if="$route.meta.showIcp" class="site-footer icp-footer" aria-label="备案信息">
      <div class="icp-info">
        <p>鄂ICP备2025123562号</p>
        <p>
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">工业和信息化部网站备案系统</a>
        </p>
      </div>
    </footer>

  </div>
</template>

<script setup lang="ts">
import {watch, computed, onMounted, onUnmounted} from "vue";
// 统一Store导入
import {useTTSStore} from "@/store";
import { useUserStore } from "@/store/user";
import { useNotificationStore } from "@/store/notification";
import { useTheme } from "@/composables/useTheme";
import { useMessage } from "@/composables/useMessage";
import { invariantDev } from "@/utils/invariant";

// 导入AppHeader组件
import { AppHeader } from "@/layouts/app-header";

// 使用新的模块化Store
const ttsStore = useTTSStore();
const userStore = useUserStore();
const notificationStore = useNotificationStore();
const theme = useTheme();
const message = useMessage();

// Fail-Fast检查 - 确保关键依赖已初始化
invariantDev(ttsStore, 'TTSStore must be available');
invariantDev(userStore, 'UserStore must be available');
invariantDev(notificationStore, 'NotificationStore must be available');
invariantDev(theme, 'Theme composable must be available');
invariantDev(theme.isDarkTheme, 'Theme state must be initialized');

// 添加缺失的状态
const isSSMLMode = computed(() => ttsStore.isSSMLMode);

// 监听登录状态，管理通知轮询
watch(() => userStore.isLoggedIn, (isLoggedIn) => {
  if (isLoggedIn) {
    notificationStore.initialize();
  } else {
    notificationStore.cleanup();
  }
}, { immediate: true });

// 监听SSML模式变化
watch(isSSMLMode, (newValue) => {
  if (!newValue) {
    // 切换到纯文本模式时，重置手动编辑状态
    ttsStore.inputs.isSSMLManuallyEdited = false;
  } 
});

// AppHeader 事件处理函数
const handleThemeToggle = () => {
  theme.toggleTheme();
};

const handleOpenApiSite = () => {
  console.log('handleOpenApiSite 被调用');
  window.open('https://api.tts88.top', '_blank');
};

const handleLogout = async () => {
  try {
    await userStore.logout();
    // 消息已在 userStore.logout() 中显示，这里不需要重复显示
  } catch (error) {
    message.showError('退出登录失败');
  }
};

onMounted(() => {
  // 初始化通知（已登录时）
});

onUnmounted(() => {
  // 组件卸载时清理通知轮询
  notificationStore.cleanup();
});
</script>

<style>
/* 全局样式导入 */
@import '@/assets/styles/main/MainStyles.css';
</style>

<style scoped>
/* 组件作用域样式导入 */
@import '@/assets/styles/main/MainScopedStyles.css';

.router-view {
  width: 100%;
}

/* 主界面容器 */
.modern-main {
  padding: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: transparent;
  /* 移除 overflow-x: auto，让 body 处理滚动 */
  /* overflow-x: auto; */ 
  /* min-width: 1200px; */
  min-width: fit-content; /* 自适应内容宽度，防止内容被截断 */
}

/* 两列布局 */
.two-column-layout {
  display: flex;
  width: 100%;
  max-width: 1600px; /* 限制最大宽度 */
  margin: 0 auto; /* 居中显示 */
  justify-content: stretch;
  background-color: transparent;
  /* overflow-x: auto; */
  overflow-y: hidden; /* 保持纵向不滚动 */
  position: relative;
  flex: 1;
}



/* 移除 1050px 断点的 flex-direction: column 切换，保持横向布局 */
/* 这样可以避免批量转换页面的布局被破坏 */
/*
@media (max-width: 1050px) {
  .two-column-layout {
    flex-direction: column;
  }
}
*/

@media (max-width: 768px) {
  .modern-main {
    padding: 0;
  }

  .two-column-layout {
    padding: 0;
  }
}

/* 页脚样式 */
.site-footer {
  margin: 20px auto 0;
  width: 100%;
  max-width: 1600px;
  padding: 20px;
  background-color: var(--tts-card-bg);
  border-radius: var(--tts-radius-lg);
  box-shadow: var(--tts-shadow-base);
  border: 1px solid var(--tts-border-base);
}

.icp-footer {
  text-align: center;
  color: #888;
}

.icp-info {
  margin: 0;
  padding: 12px 0;
  font-size: 14px;
  color: #888;
}

.icp-info a {
  color: #888;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .site-footer {
    margin-top: 10px;
    border-radius: var(--tts-radius-base);
    padding: 15px;
  }
}

/* Layer A: 背景画布层 */
.app-background-layer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100vh; /* Cover full height */
  background-color: var(--tts-bg-canvas); /* Base canvas color */
  background-image: var(--tts-bg-canvas-tint); /* Top gradient tint */
  z-index: 0;
  pointer-events: none;
}

/* 暗色主题背景已移除 - 强制浅色模式 */

/* Ensure content sits above background */
.app-header {
  z-index: 100; /* Higher than background */
}

.two-column-layout {
  z-index: 10;
  position: relative;
}
</style>
